import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';

import { App, Button, Col, Form, Input, Radio, Row, Select } from 'antd';
import { StatusCodes } from 'http-status-codes';

import CountryPreview from 'components/CountryPreview/CountryPreview';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import PhoneSelect from 'components/PhoneSelect/PhoneSelect';
import ReCaptcha, { ReCaptchaRef } from 'components/ReCaptcha/ReCaptcha';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import useErrorCatch from 'hooks/useErrorCatch';
import { useTrackForm } from 'hooks/useTrackForm';

import formatAndOrderData from 'utils/formatAndOrderData';
import Services from 'utils/services';

import allCountries from 'constants/allCountries';
import { SECTION_ROW_GUTTER } from 'constants/commons';
import { DOMESTIC_OVERSEAS_SORT } from 'constants/commons';
import { GTMEvents } from 'constants/enums';
import { Dictionaries } from 'constants/enums';
import { ENQUIRY_FORM_DUPLICATED_ERROR } from 'constants/messages';

import { ReactComponent as ArrowRight } from 'assets/icons/arrowRight.svg';

import styles from './InquiryForm.module.scss';

type InquiryFormProps = {
  setIsShowThankYouMessage: Dispatch<SetStateAction<boolean>>;
};

const InquiryForm: FC<InquiryFormProps> = ({ setIsShowThankYouMessage }) => {
  const catchError = useErrorCatch();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const recaptchaRef = useRef<ReCaptchaRef>(null);
  const fieldsValues = Form.useWatch([], form);
  const COUNTRIES_ORDER = ['USA', 'Canada'];

  useTrackForm({
    formId: 'inquiry',
    incompleteEvent: GTMEvents.AboutFormAbandonment,
    submittedEvent: GTMEvents.AboutFormSubmit
  });

  const industries = useSelector(selectDictionary(Dictionaries.Industry));

  const domesticOverseasDict = useSelector(
    selectDictionary(Dictionaries.ClientDomesticOver)
  );

  const options = allCountries.map(({ code, name }) => ({
    value: code,
    label: name
  }));

  const sortedOptions = formatAndOrderData(options, COUNTRIES_ORDER);

  const countriesOptions = sortedOptions.map(({ value, label }) => ({
    label: <CountryPreview countryCode={value} countryName={label} />,
    value: value
  }));

  const domesticOverseasOptions = useMemo(() => {
    return formatAndOrderData(domesticOverseasDict, DOMESTIC_OVERSEAS_SORT);
  }, [domesticOverseasDict, DOMESTIC_OVERSEAS_SORT]);

  const handleSubmitForm = async () => {
    try {
      await form.validateFields();

      if (recaptchaRef.current) {
        recaptchaRef.current.execute();
      }
    } catch (e) {
      catchError({
        default: { message: 'Please fill in all required fields correctly' }
      });
      return;
    }
  };

  const handleCaptchaSuccess = async () => {
    setIsLoading(true);
    const data = form.getFieldsValue();

    const transformedData = transformFormData(data);

    try {
      await Services.postInquiry(transformedData);
      setIsShowThankYouMessage(true);
      form.resetFields();
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } catch (error) {
      if (
        ((error as { code: number }).code = StatusCodes.UNPROCESSABLE_ENTITY)
      ) {
        catchError({ default: { message: ENQUIRY_FORM_DUPLICATED_ERROR } });
      } else {
        catchError({
          default: { message: "Can't submit form, please try again later" }
        });
      }
    } finally {
      setIsLoading(false);
      resetThankYouMessage();
    }
  };

  const onCaptchaChange = async (value: string | null): Promise<void> => {
    if (value) {
      handleCaptchaSuccess();
    }
  };

  const handleExpired = (): void => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleError = (): void => {
    console.error('reCAPTCHA error occurred');
  };

  const resetThankYouMessage = (): NodeJS.Timeout => {
    return setTimeout(() => {
      setIsShowThankYouMessage(false);
    }, 5000);
  };

  const transformFormData = (data: any) => {
    const transformedData = {
      ...data,
      pocContacts: [
        {
          pocEmail: data.pocEmail,
          pocPhone: data.pocPhone,
          pocName: data.pocName
        }
      ]
    };

    delete transformedData.pocEmail;
    delete transformedData.pocPhone;
    delete transformedData.pocName;

    return transformedData;
  };

  useEffect(() => {
    const timer = resetThankYouMessage();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    form.validateFields({ validateOnly: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsValues]);

  return (
    <div>
      <Form
        id="inquiry"
        form={form}
        scrollToFirstError
        name="contactUs"
        autoComplete="off"
        onFinish={handleSubmitForm}
      >
        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col md={12} xs={24}>
            <Form.Item
              name="clientName"
              label="Company name"
              rules={[{ required: true }]}
            >
              <Input
                placeholder="Please enter Company name"
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item name="companyWebsite" label="Company website">
              <Input
                placeholder="Please enter Company website"
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col md={12} xs={24}>
            <Form.Item
              name="industry"
              label="Industry"
              rules={[{ required: true, message: 'Please select Industry!' }]}
            >
              <Select
                placeholder="Select Industry"
                options={industries}
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              name="country"
              label="Country"
              rules={[{ required: true, message: 'Please select Country!' }]}
              className={styles.field}
            >
              <Select
                placeholder="Please select Country"
                options={countriesOptions}
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col md={8} xs={24}>
            <Form.Item
              name="pocName"
              label="Point of Contact"
              rules={[{ required: true }]}
            >
              <Input
                placeholder="First Name + Last Name"
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item
              name="pocEmail"
              label="Work E-mail"
              rules={[
                {
                  type: 'email',
                  required: true,
                  message: 'email is not a valid'
                }
              ]}
            >
              <Input
                placeholder="Please fill work e-mail address"
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item
              name="pocPhone"
              label="Work Phone Number"
              rules={[{ required: true }]}
            >
              <PhoneSelect
                name="pocPhone"
                form={form}
                className="phone-select"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <h4 className={styles.sectionTitle}>Additional info</h4>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col md={12} xs={24}>
            <Form.Item name="numberOfOpenings" label="Number of job openings">
              <Input
                placeholder="Please fill number of openings"
                disabled={isLoading}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item name="startDate" label="Expected start date">
              <CustomDatePicker name="startDate" form={form} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}>
          <Col span={24}>
            <Form.Item
              name="domesticOverseas"
              label="Domestic of international workers?"
              className={styles.bigLabel}
            >
              <Radio.Group
                options={domesticOverseasOptions}
                className="vertical-list"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.row}></Row>

        <div className={styles.submitContainer}>
          <Button
            className={styles.submitButton}
            type="primary"
            icon={<ArrowRight />}
            iconPosition="end"
            htmlType="submit"
            loading={isLoading}
          >
            Submit
          </Button>
        </div>
        <ReCaptcha
          ref={recaptchaRef}
          onChange={onCaptchaChange}
          onError={handleError}
          onExpired={handleExpired}
        />
      </Form>
    </div>
  );
};

export default InquiryForm;
